import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { useAdminAuth } from "@/context/AdminAuthContext";

const EMPTY = {
  slug_es: "",
  slug_en: "",
  title_es: "",
  title_en: "",
  excerpt_es: "",
  excerpt_en: "",
  category_es: "",
  category_en: "",
  body_es: [],
  body_en: [],
  hero_image: "",
  date: new Date().toISOString().slice(0, 10),
  read_time: "",
  published: true,
};

function fieldStyle(err) {
  return `w-full bg-cream-100 border ${err ? "border-red-700" : "border-cream-400"} px-3 py-2 text-olive-900 text-sm font-light placeholder:text-olive-800/40 focus:outline-none focus:border-olive-700 transition-colors`;
}
const labelStyle = "block text-[10px] uppercase tracking-[0.24em] text-olive-800/60 mb-1.5";

/**
 * Body paragraphs are edited as a single textarea separated by blank lines
 * (two consecutive newlines). This lets the owner write plain prose without
 * having to think about arrays.
 */
function bodyToText(paras) {
  return (paras || []).join("\n\n");
}
function textToBody(txt) {
  return (txt || "")
    .split(/\n{2,}/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export default function BlogEditor({ post, onClose, onSaved }) {
  const { authFetch } = useAdminAuth();
  const isEdit = Boolean(post?.id);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [topError, setTopError] = useState(null);
  // Local mirror of the body textareas so intermediate whitespace is preserved
  // during typing (we only re-split when we submit).
  const [bodyEsText, setBodyEsText] = useState("");
  const [bodyEnText, setBodyEnText] = useState("");

  useEffect(() => {
    if (post) {
      setForm({ ...EMPTY, ...post });
      setBodyEsText(bodyToText(post.body_es));
      setBodyEnText(bodyToText(post.body_en));
    } else {
      setForm(EMPTY);
      setBodyEsText("");
      setBodyEnText("");
    }
    setErrors({});
    setTopError(null);
  }, [post]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!/^[a-z0-9-]{2,140}$/.test(form.slug_es)) e.slug_es = true;
    if (!/^[a-z0-9-]{2,140}$/.test(form.slug_en)) e.slug_en = true;
    if (!form.title_es || form.title_es.length < 2) e.title_es = true;
    if (!form.title_en || form.title_en.length < 2) e.title_en = true;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(form.date)) e.date = true;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    setTopError(null);
    if (!validate()) {
      setTopError("Revisa los campos marcados.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        body_es: textToBody(bodyEsText),
        body_en: textToBody(bodyEnText),
      };
      if (isEdit) {
        await authFetch(`/admin/blog/posts/${post.id}`, { method: "PUT", data: payload });
      } else {
        await authFetch(`/admin/blog/posts`, { method: "POST", data: payload });
      }
      onSaved?.();
      onClose?.();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Error al guardar.";
      setTopError(detail);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="blog-editor"
      className="fixed inset-0 z-[9999] bg-charcoal/60 flex items-start justify-center overflow-y-auto p-4 sm:p-8"
    >
      <div className="bg-cream-100 border border-cream-400 max-w-3xl w-full">
        <div className="flex items-center justify-between p-5 border-b border-cream-400">
          <h2 className="font-serif text-xl text-olive-900">
            {isEdit ? "Editar publicación" : "Nueva publicación"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            data-testid="blog-editor-close"
            className="text-olive-800/60 hover:text-olive-900"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 sm:p-8 space-y-6">
          {topError && (
            <div className="bg-red-50 border border-red-300 px-4 py-2.5 text-red-800 text-sm">
              {topError}
            </div>
          )}

          <div className="grid sm:grid-cols-3 gap-4">
            <div className="sm:col-span-2">
              <label className={labelStyle}>Slug ES (URL /blog/…)</label>
              <input
                value={form.slug_es}
                onChange={(e) => set("slug_es", e.target.value.toLowerCase())}
                placeholder="que-es-aove-cosecha-temprana"
                data-testid="blog-slug-es"
                className={fieldStyle(errors.slug_es)}
              />
            </div>
            <div>
              <label className={labelStyle}>Estado</label>
              <select
                value={form.published ? "published" : "draft"}
                onChange={(e) => set("published", e.target.value === "published")}
                data-testid="blog-published"
                className={fieldStyle(false)}
              >
                <option value="published">Publicado</option>
                <option value="draft">Borrador</option>
              </select>
            </div>
          </div>

          <div>
            <label className={labelStyle}>Slug EN (URL /journal/…)</label>
            <input
              value={form.slug_en}
              onChange={(e) => set("slug_en", e.target.value.toLowerCase())}
              placeholder="what-is-early-harvest-evoo"
              data-testid="blog-slug-en"
              className={fieldStyle(errors.slug_en)}
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className={labelStyle}>Título (ES)</label>
              <input
                value={form.title_es}
                onChange={(e) => set("title_es", e.target.value)}
                data-testid="blog-title-es"
                className={fieldStyle(errors.title_es)}
              />
            </div>
            <div>
              <label className={labelStyle}>Título (EN)</label>
              <input
                value={form.title_en}
                onChange={(e) => set("title_en", e.target.value)}
                data-testid="blog-title-en"
                className={fieldStyle(errors.title_en)}
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className={labelStyle}>Categoría (ES)</label>
              <input
                value={form.category_es}
                onChange={(e) => set("category_es", e.target.value)}
                placeholder="Guía del AOVE"
                className={fieldStyle(false)}
              />
            </div>
            <div>
              <label className={labelStyle}>Categoría (EN)</label>
              <input
                value={form.category_en}
                onChange={(e) => set("category_en", e.target.value)}
                placeholder="EVOO guide"
                className={fieldStyle(false)}
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className={labelStyle}>Extracto (ES)</label>
              <textarea
                rows={3}
                value={form.excerpt_es}
                onChange={(e) => set("excerpt_es", e.target.value)}
                className={fieldStyle(false)}
              />
            </div>
            <div>
              <label className={labelStyle}>Extracto (EN)</label>
              <textarea
                rows={3}
                value={form.excerpt_en}
                onChange={(e) => set("excerpt_en", e.target.value)}
                className={fieldStyle(false)}
              />
            </div>
          </div>

          <div>
            <label className={labelStyle}>
              Cuerpo ES (separa cada párrafo con una línea en blanco)
            </label>
            <textarea
              rows={10}
              value={bodyEsText}
              onChange={(e) => setBodyEsText(e.target.value)}
              data-testid="blog-body-es"
              className={fieldStyle(false)}
            />
          </div>

          <div>
            <label className={labelStyle}>
              Body EN (blank line between paragraphs)
            </label>
            <textarea
              rows={10}
              value={bodyEnText}
              onChange={(e) => setBodyEnText(e.target.value)}
              data-testid="blog-body-en"
              className={fieldStyle(false)}
            />
          </div>

          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <label className={labelStyle}>Fecha (YYYY-MM-DD)</label>
              <input
                type="date"
                value={form.date}
                onChange={(e) => set("date", e.target.value)}
                data-testid="blog-date"
                className={fieldStyle(errors.date)}
              />
            </div>
            <div>
              <label className={labelStyle}>Tiempo de lectura</label>
              <input
                value={form.read_time}
                onChange={(e) => set("read_time", e.target.value)}
                placeholder="5 min"
                className={fieldStyle(false)}
              />
            </div>
          </div>

          <div>
            <label className={labelStyle}>Imagen destacada (URL)</label>
            <input
              value={form.hero_image}
              onChange={(e) => set("hero_image", e.target.value)}
              placeholder="https://…"
              data-testid="blog-hero"
              className={fieldStyle(false)}
            />
            {form.hero_image ? (
              <div className="mt-3 w-40 h-24 bg-cream-200 border border-cream-400 overflow-hidden">
                <img
                  src={form.hero_image}
                  alt={form.title_es || "preview"}
                  className="h-full w-full object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              </div>
            ) : null}
          </div>
        </div>

        <div className="p-5 border-t border-cream-400 flex items-center justify-end gap-3 bg-cream-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-olive-700/40 text-[11px] uppercase tracking-[0.24em] text-olive-800 hover:bg-cream-100"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            data-testid="blog-save"
            className="px-5 py-2.5 bg-olive-700 text-cream-100 text-[11px] uppercase tracking-[0.24em] hover:bg-olive-900 disabled:opacity-60"
          >
            {saving ? "Guardando…" : isEdit ? "Guardar cambios" : "Crear publicación"}
          </button>
        </div>
      </div>
    </div>
  );
}
